/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    useCallback, useEffect, useRef, useState,
} from 'react';
import {
    Navigate, useNavigate,
} from 'react-router-dom';
import { FormikHelpers, FormikValues } from 'formik';
import {
    Container,
    Row,
    Col,
    Form,
    Button,
} from 'react-bootstrap';
import { isEqual } from 'lodash';
import { SeverityLevel } from '@microsoft/applicationinsights-common';
import { ErrorType, WizardRoutedStepProps, WizardStepProps } from './types';
import SteppedNavigation from '../../SteppedNavigation';
import PreviousStepButton from './PreviousStepButton';
import NextStepButton from './NextStepButton';
import ErrorSummary from '../ErrorSummary';
import { HttpStatusCode, InitialValue } from '../../../types';
import { FormStepStatus, ProblemDetails, ValidationProblemDetails } from '../../../api/web-api-client';
import FormikForm from '../FormikForm';
import { nullOrUndefinedToEmpty } from '../../../utils';
import useHtmlTitle from '../../Utilities/useHtmlTitle';
import useBodyClass from '../../Utilities/useBodyClass';
import { setDashboardNotification, setGetStartedNotification } from '../../../storage/notification';
import { NotificationSeverity } from '../../../storage/types';
import useAccountContext from '../../../authentication/hooks';
import { env } from '../../../env';
import AppLogger from '../../../instrumentation/AppLogger';

interface StepState<T extends FormikValues> {
    values: InitialValue<T>;
}

const WizardRoutedStep = (props: WizardRoutedStepProps<FormikValues>) => {
    const {
        initialValues,
        location,
        validateHard,
        validateSoft,
        stepStatuses,
        loadStepValues,
        locationAfterExit,
        locationOnCompletion,
        currentStepIndex,
        allSteps,
        nextButtonTitle,
        title,
        lastStepNextButtonTitle,
        previousButtonTitle,
        url,
        hidingFields,
        onSaveAndExit,
        onSaveAndNext,
        bannerTitle,
        bannerRefTitle,
        bannerSubTitle,
        canSaveDraft,
        showSaveAndNextButton,
        showGoToDashboardButton,
        showBanner,
        getRedirectionLocationOnError,
        discard,
        confirmationOnSubmission,
        isSummaryPage,
    } = props;

    useHtmlTitle(`${bannerTitle} | NMI Services portal`);
    useBodyClass('wizard');
    // const route = useResolvedPath('').pathname;
    const navigate = useNavigate();
    const accountContext = useAccountContext();
    const controllerRef = useRef<AbortController | null>();
    const abortSignal = () => {
        const controller = new AbortController();
        controllerRef.current = controller;
    };

    const [stepState, setStepState] = useState<StepState<InitialValue<FormikValues>>>({
        values: { ...initialValues },
    });

    const [isLoading, setIsLoading] = useState(true);
    const [redirectionLocationOnError, setRedirectionLocationOnError] = useState<string | undefined>(undefined);
    const [loadingError, setLoadingError] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [noThirdPartyAccess, setNoThirdPartyAccess] = useState(false);
    const [gone, setGone] = useState(false);
    const [concurrencyError, setConcurrencyError] = useState(false);
    const [isWafViolation, setisWafViolation] = useState(false);
    const [serverError, setServerError] = useState<ProblemDetails | ValidationProblemDetails>();

    const loadData = useCallback(async () => {
        setRedirectionLocationOnError(undefined);
        setLoadingError(false);
        setConcurrencyError(false);
        try {
            abortSignal();
            const currentStep = await loadStepValues(controllerRef.current?.signal);
            const values = nullOrUndefinedToEmpty(currentStep.stepValues);
            setStepState({
                values,
            });
        } catch (error) {
            const err = error as Error;
            AppLogger.error('Could not load data for form step.', err);
            const loadServerError = error as ProblemDetails;
            AppLogger.trace('Could not load data for form step.', SeverityLevel.Error, { problemDetails: loadServerError, err });
            if (getRedirectionLocationOnError && loadServerError?.status) {
                const locationOnError = getRedirectionLocationOnError(loadServerError.status, ErrorType.Load);
                setRedirectionLocationOnError(locationOnError);
            }

            if (!redirectionLocationOnError) {
                if (loadServerError.status === HttpStatusCode.NotFound) {
                    setNotFound(true);
                } else if (loadServerError.status === HttpStatusCode.Forbidden
                            && loadServerError.title
                            && loadServerError.title.includes('No third-party access')) {
                    setNoThirdPartyAccess(true);
                } else if (loadServerError.status === HttpStatusCode.Forbidden) {
                    setServerError(loadServerError);
                } else if (loadServerError.status === HttpStatusCode.Gone) {
                    setGone(true);
                }
                // TODO: Figure out what we do with this error
                // else if ((error as DOMException)?.code !== DOMException.ABORT_ERR) {
                //     setLoadingError(true);
                // }
            }
            AppLogger.error('Failed to Load step values', err);
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadStepValues]);

    useEffect(() => {
        let mounted = true;
        if (mounted) {
            loadData();
        }
        return (() => { mounted = false; });
    }, [loadData]);

    useEffect(() => {
        let mounted = true;
        if (mounted && concurrencyError) {
            loadData();
        }
        return (() => { mounted = false; });
    }, [loadData, concurrencyError]);

    const nextStep = currentStepIndex < allSteps.length - 1
        ? allSteps[currentStepIndex + 1]
        : false;

    const goToStep = (
        step: React.ReactElement<WizardStepProps<FormikValues>, string | React.JSXElementConstructor<any>>,
        baseUrl?: string,
    ) => ((baseUrl && url !== baseUrl)
        ? navigate(`${baseUrl}${step.props.location}`, { replace: true })
        : navigate(`${url}${step.props.location}`));

    const onSubmitStep = async (
        values: FormikValues,
        formikHelpers: FormikHelpers<FormikValues>,
    ) => {
        if (onSaveAndNext) {
            abortSignal();
            setRedirectionLocationOnError(undefined);
            setConcurrencyError(false);
            setServerError(undefined);
            setisWafViolation(false);
            try {
                const isDirty = !isEqual(stepState.values, values);
                const result = await onSaveAndNext(
                    values,
                    isDirty,
                    formikHelpers,
                    controllerRef.current?.signal,
                );
                // Once we have saved the step, we know it's complete.
                // Mark it done and then go to the next or final step.
                stepStatuses[currentStepIndex].status = FormStepStatus.Completed;
                if (nextStep) {
                    goToStep(nextStep, result?.baseUrl);
                } else {
                    navigate(locationOnCompletion);
                }
            } catch (error) {
                const err = error as Error;
                AppLogger.error('Could not submit form step.', error as Error, values);
                const saveServerError = error as ProblemDetails;
                AppLogger.trace('Could not submit form step.', SeverityLevel.Error, { values, problemDetails: saveServerError, err });
                if (getRedirectionLocationOnError && saveServerError?.status) {
                    const locationOnError = getRedirectionLocationOnError(saveServerError.status, ErrorType.Update);
                    setRedirectionLocationOnError(locationOnError);
                }

                if (!redirectionLocationOnError) {
                    const { headers } = error as any;
                    const server = headers.server as string;
                    if (saveServerError.status === HttpStatusCode.NotFound) {
                        setNotFound(true);
                    } else if (saveServerError.status === HttpStatusCode.Forbidden
                                && server && server.startsWith('Microsoft-Azure-Application-Gateway')) {
                        setServerError(saveServerError);
                        setisWafViolation(true);
                    } else if (saveServerError.status === HttpStatusCode.Forbidden
                                && saveServerError.title
                                && saveServerError.title.includes('No third-party access')) {
                        setNoThirdPartyAccess(true);
                    } else if (saveServerError.status === HttpStatusCode.Forbidden) {
                        setServerError(saveServerError);
                    } else if (saveServerError.status === HttpStatusCode.Gone) {
                        setGone(true);
                    } else {
                        setServerError(saveServerError);
                        if (saveServerError.status === HttpStatusCode.Conflict) {
                            setConcurrencyError(true);
                        }
                    }
                }
            }
        }
    };

    const onSaveAndExitStep = async (
        values: FormikValues,
        formikHelpers: FormikHelpers<FormikValues>,
    ) => {
        if (onSaveAndExit) {
            abortSignal();
            setRedirectionLocationOnError(undefined);
            setConcurrencyError(false);
            setServerError(undefined);
            setisWafViolation(false);
            try {
                const isDirty = !isEqual(stepState.values, values);
                await onSaveAndExit(
                    values,
                    isDirty,
                    formikHelpers,
                    controllerRef.current?.signal,
                );
                navigate(locationAfterExit || '/');
            } catch (error) {
                const err = error as Error;
                AppLogger.error('Could not save form step.', err, values);
                const saveServerError = error as ProblemDetails;
                AppLogger.trace('Could not save form step.', SeverityLevel.Error, { values, problemDetails: saveServerError, err });
                if (getRedirectionLocationOnError && saveServerError?.status) {
                    const locationOnError = getRedirectionLocationOnError(saveServerError.status, ErrorType.Update);
                    setRedirectionLocationOnError(locationOnError);
                }

                if (!redirectionLocationOnError) {
                    if (saveServerError.status === HttpStatusCode.NotFound) {
                        setNotFound(true);
                    } else if (saveServerError.status === HttpStatusCode.Forbidden
            && saveServerError.title
            && saveServerError.title.includes('No third-party access')) {
                        setNoThirdPartyAccess(true);
                    } else if (saveServerError.status === HttpStatusCode.Forbidden) {
                        setServerError(saveServerError);
                    } else if (saveServerError.status === HttpStatusCode.Gone) {
                        setGone(true);
                    } else {
                        setServerError(saveServerError);
                        if (saveServerError.status === HttpStatusCode.Conflict) {
                            setConcurrencyError(true);
                        }
                    }
                }
            }
        } else {
            navigate(locationAfterExit || '/');
        }
    };

    const onCancelClick = () => {
        if (discard?.onDiscard) {
            discard.onDiscard();
        }
        // New option for Wizard footer Cancel button
        if (discard?.onCancel) {
            discard.onCancel();
        }
        if (discard?.locationOnDiscard.startsWith('https://')) window.location.replace(env.EXTERNAL_REDIRECT_URL);
        navigate((discard?.locationOnCancel || discard?.locationOnDiscard) || '/');
    };

    if (redirectionLocationOnError) {
        return <Navigate to={`${redirectionLocationOnError}`} />;
    }

    if (notFound) {
        return <Navigate to='/not-found' />;
    }

    if (loadingError) {
        return <Navigate to='/server-error' />;
    }

    if (noThirdPartyAccess) {
        setDashboardNotification({
            message: 'You no longer have access to the records for'
        + ` ${accountContext?.details?.targetOrganisation?.targetOrganisationName}. Your changes have not been saved.`,
            severity: NotificationSeverity.Error,
        });
        if (accountContext) {
            accountContext.setTargetOrganisation(
                accountContext?.details?.abn ?? '',
                accountContext?.details?.organisation ?? '',
            );
        }
        return <Navigate to='/dashboard' />;
    }

    if (gone) {
        setGetStartedNotification({
            message: 'You currently do not have access. Please sign in again.',
            severity: NotificationSeverity.Error,
        });
        return <Navigate to='/sign-out' />;
    }

    if (!isLoading && currentStepIndex > 0) {
        const firstIncompleteStepIndex = stepStatuses.findIndex((x) => x.status !== FormStepStatus.Completed);
        if (firstIncompleteStepIndex !== -1 && firstIncompleteStepIndex < currentStepIndex) {
            return <Navigate to={`${url}${allSteps[firstIncompleteStepIndex].props.location}`} />;
        }
    }

    return (
        <FormikForm<FormikValues>
            initialValues={stepState.values}
            onSubmit={onSubmitStep}
            validateHard={validateHard}
            validateSoft={validateSoft}
            isLoading={isLoading}
            promptPath={`${url}${location}`}
            hidingFields={hidingFields}
            onSaveAndExit={onSaveAndExitStep}
            bannerTitle={bannerTitle}
            bannerRefTitle={bannerRefTitle}
            bannerSubTitle={bannerSubTitle}
            canSaveDraft={canSaveDraft}
            showGoToDashboardButton={showGoToDashboardButton}
            showBanner={showBanner || showBanner === undefined}
            discard={discard}
            isSummaryPage={isSummaryPage}
        >
            { (formik) => (
                <Container fluid id='main' role='main' className='px-0' tabIndex={-1} aria-busy={isLoading} aria-live='polite' aria-atomic='true'>
                    <Container className='py-5'>
                        <Row>
                            { allSteps.length > 1 && (
                                <SteppedNavigation
                                    id='wizard-stepper-id'
                                    activeStep={currentStepIndex}
                                    steps={allSteps.map((s, i) => ({
                                        completed: stepStatuses[i].status === FormStepStatus.Completed,
                                        path: `${url}${s.props.location}`,
                                        title: s.props.title,
                                    }))}
                                />
                            )}
                        </Row>
                        <Row className='mb-5'>
                            <Col sm={12} md={10} lg={8} className='mx-auto'>
                                <h1 id='page-title' tabIndex={-1}>
                                    {allSteps.length > 1 && (
                                        <span className='visually-hidden'>
                                            {`Step ${currentStepIndex + 1} of ${allSteps.length} `}
                                        </span>
                                    )}
                                    {title}
                                </h1>
                                {!isSummaryPage && (
                                    <p id='form-inst' className='visually-hidden'>
                                        Form instructions: All form fields are required unless marked optional.
                                    </p>
                                )}
                                <ErrorSummary
                                    serverErrors={serverError}
                                    prefixToRemove='formStep.'
                                    disableLinkedError={isSummaryPage}
                                    isWafViolation={isWafViolation}
                                />
                                <Form
                                    data-testid='form'
                                    onSubmit={formik.handleSubmit}
                                    autoComplete='off'
                                >
                                    {allSteps[currentStepIndex]}
                                    <div className='mt-5 d-grid w-100 gap-3 d-md-flex justify-content-md-between'>
                                        <PreviousStepButton
                                            currentStepIndex={currentStepIndex}
                                            steps={allSteps.slice()}
                                            title={previousButtonTitle}
                                            url={url}
                                            className='me-md-auto order-2 order-md-0'
                                        />
                                        {discard?.showCancelButton ? (
                                            <Button
                                                data-testid='cancel-button'
                                                // title={discard.cancelButtonTitle}
                                                // aria-label={discard.cancelButtonTitle}
                                                onClick={() => onCancelClick()}
                                                variant='tertiary'
                                                className='me-md-auto order-2 order-md-0'
                                            >
                                                <i className='icon-close me-1' aria-hidden='true' />
                                                {discard.cancelButtonTitle}
                                            </Button>
                                        ) : null}
                                        {showSaveAndNextButton !== false ? (
                                            <NextStepButton
                                                currentStepIndex={currentStepIndex}
                                                steps={allSteps.slice()}
                                                title={nextButtonTitle}
                                                finalStepTitle={lastStepNextButtonTitle}
                                                finalStepConfirmation={confirmationOnSubmission}
                                                className='ms-md-auto'
                                            />
                                        ) : null}
                                    </div>
                                </Form>
                            </Col>
                        </Row>
                    </Container>
                </Container>
            )}
        </FormikForm>
    );
};

export default WizardRoutedStep;
